/* eslint-disable max-lines */
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddressInput,
  AddressTypeInput,
  ChannelType,
  State,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { useNavigation } from 'expo-router/react-navigation';
import useChangeAddress from 'features/myVaro/hooks/useChangeAddress';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import AddressVerificationScreen from 'features/personalDetails/screens/AddressVerification/AddressVerificationScreen';
import { createChangeAddressAnalytics } from 'features/personalDetails/screens/ChangeAddress/helpers/changeAddressAnalytics';
import areAddressesIdentical from 'features/personalDetails/utils/areAddressesIdentical';
import { SmartyStreetsResponseItem } from 'features/signup/helpers/smartyStreets';
import useSmartyStreets from 'features/signup/hooks/useSmartyStreets';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GetStates from 'services/graphql/queries/States';
import Button from 'shared/components/vxl/Button/Button';
import { addressSchema } from 'shared/components/vxl/input/schemas/address.schema';
import { PO_BOX_REGEX } from 'shared/components/vxl/input/schemas/streetAddressLineOne.schema';
import VXLDropdown, {
  VXL_DROPDOWN_MENU_RESERVED_SPACE,
} from 'shared/components/vxl/input/VXLDropdown/VXLDropdown';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import { logBreadcrumb } from 'shared/utils/sentry/sentry';
import {
  findFullStateString,
  getState,
  getStateAbbreviation,
} from 'shared/utils/smartyStreet/smartyStreet';
import { useQuery } from 'urql';

enum ActionType {
  GOBACK = 'GO_BACK',
}

const isPOBox = (str?: string): boolean =>
  !str ? false : PO_BOX_REGEX.test(str);

const ANALYTICS_KEY_MAP: Record<string, string> = {
  changeAddress: 'changeAddress',
  changeMailingAddress: 'changeMailingAddress',
  editAddress: 'editAddress',
  editHomeAddress: 'edit-home-address',
};

type ChangeAddressScreenProps = {
  screenType: 'homeAddressScreen' | 'mailingAddressScreen';
};

const ChangeAddressScreen = ({ screenType }: ChangeAddressScreenProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'personalDetails',
  });
  const insets = useSafeAreaInsets();
  const isHomeAddressScreenType = screenType === 'homeAddressScreen';
  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const { result, fetching } = useGetProfileInfo();
  const customerInfo = result?.customerInfo ?? null;
  const { address: addressOnFile, mailingAddress: mailingAddressOnFile } =
    customerInfo || {};

  const selectedAddress = isHomeAddressScreenType
    ? addressOnFile
    : mailingAddressOnFile;

  const isMailingAddressDifferentThanResidential =
    customerInfo?.isMailingAddressDifferentThanResidential ?? false;

  const { updatedAddressList } = useContext(MyVaroContext);
  const {
    isCheckedHomeAndMailingAddress,
    setIsCheckedHomeAndMailingAddress,
    setIsIdenticalHomeAndMailingAddress,
  } = useContext(MyVaroContext);
  const [isCheckboxDirty, setIsCheckboxDirty] = useState(false);

  useEffect(() => {
    setIsCheckboxDirty(
      isCheckedHomeAndMailingAddress !==
        isMailingAddressDifferentThanResidential,
    );
  }, [
    isCheckedHomeAndMailingAddress,
    isMailingAddressDifferentThanResidential,
  ]);

  useEffect(() => {
    if (isCheckedHomeAndMailingAddress === undefined) {
      setIsCheckedHomeAndMailingAddress?.(
        isMailingAddressDifferentThanResidential,
      );
    }
  }, [
    isMailingAddressDifferentThanResidential,
    isCheckedHomeAndMailingAddress,
    setIsCheckedHomeAndMailingAddress,
  ]);

  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

  const isHomeScreenType = screenType === 'homeAddressScreen';
  const pathname = usePathname();
  const navigation = useNavigation();

  const {
    control,
    getValues,
    reset,
    setValues,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      addressLine1: selectedAddress?.addressLine1 || '',
      addressLine2: selectedAddress?.addressLine2 || '',
      city: selectedAddress?.city || '',
      state: selectedAddress?.state || '',
      zip: selectedAddress?.zip || '',
    },
    resolver: zodResolver(addressSchema),
  });

  const getButtonText =
    !isMailingAddressDifferentThanResidential &&
    isCheckedHomeAndMailingAddress &&
    isCheckboxDirty &&
    isHomeAddressScreenType
      ? translatedText('address.buttons.saveAndContinue')
      : translatedText('address.buttons.save');

  const screenTitle = isHomeScreenType
    ? translatedText('changeAddress.screenTitle')
    : translatedText('changeMailingAddress.screenTitle');

  useEffect(() => {
    setValues({
      addressLine1: selectedAddress?.addressLine1 || '',
      addressLine2: selectedAddress?.addressLine2 || '',
      city: selectedAddress?.city || '',
      state: selectedAddress?.state || '',
      zip: selectedAddress?.zip || '',
    });
    // Initialize searchTerms with existing address to enable autocomplete on focus
    if (selectedAddress?.addressLine1) {
      setSearchTerms(selectedAddress.addressLine1);
    }
  }, [
    selectedAddress?.addressLine2,
    selectedAddress?.city,
    selectedAddress?.state,
    selectedAddress?.zip,
    selectedAddress?.addressLine1,
    setValues,
  ]);

  useEffect(() => {
    const shouldClear =
      !isMailingAddressDifferentThanResidential && !isHomeAddressScreenType;

    if (shouldClear) {
      reset({
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
      });
    }
  }, [
    isMailingAddressDifferentThanResidential,
    isCheckedHomeAndMailingAddress,
    reset,
    isCheckboxDirty,
    isHomeAddressScreenType,
  ]);

  useEffect(() => {
    if (showBottomSheet) {
      bottomSheetRef?.current?.present();
    }
  }, [showBottomSheet]);

  const [{ data: statesData, fetching: loadingStates }] = useQuery<{
    states: State[];
  }>({
    query: GetStates,
    requestPolicy: 'cache-and-network',
  });

  const { isVloc, isLimitedAccess, fromConfirmAddress, analyticsKey } =
    useLocalSearchParams<{
      isVloc: string;
      isLimitedAccess: string;
      fromConfirmAddress: string;
      analyticsKey: string;
    }>();

  const { loading, setUpdatedAddress, refetchValidateAddress } =
    useChangeAddress({
      channelType: ChannelType.Customer,
      isVloc: isVloc === 'true',
      isLimitedAccess: isLimitedAccess === 'true',
      fromConfirmAddress: fromConfirmAddress === 'true',
      screenType,
      setShowBottomSheet,
    });

  useViewDidFocusScreenAnalyticsEvent({
    key: ANALYTICS_KEY_MAP[analyticsKey] ?? 'changeAddress',
  });

  let changeAddressSubmitClickEvent = 'saveAddressClicked';
  if (isVloc) {
    changeAddressSubmitClickEvent = 'changeAddressSubmit';
  }
  if (fromConfirmAddress) {
    changeAddressSubmitClickEvent = 'saveHomeAddressClicked';
  }

  const [searchTerms, setSearchTerms] = useState('');
  const [selectedSearchTerms, setSelectedSearchTerms] =
    useState<SmartyStreetsResponseItem>();
  const { suggestions } = useSmartyStreets(searchTerms, selectedSearchTerms);
  const filteredSuggestions = isHomeAddressScreenType
    ? suggestions?.filter((item) => !isPOBox(item.street_line))
    : suggestions;

  const { isFeatureEnabled } = useStatsig();
  const isMailingScreenEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_CUSTOMER_SERVICE_HOME_MAILING_ADDRESS,
  );
  const isMailingSectionVisible =
    isMailingScreenEnabled && isHomeAddressScreenType && !isLimitedAccess;

  const { logMailingAddressCheckboxChange, logAddressSubmit } =
    createChangeAddressAnalytics({
      isCheckedHomeAndMailingAddress,
      isHomeScreenType,
      pathname,
    });

  const hasAddressChanged: () => boolean = useCallback(() => {
    const formData = getValues();

    return !areAddressesIdentical(
      { ...formData, country: 'US' },
      selectedAddress,
    );
  }, [getValues, selectedAddress]);

  const shouldNavigateToMailingAddress =
    isCheckedHomeAndMailingAddress &&
    !isDirty &&
    isCheckboxDirty &&
    isHomeScreenType &&
    isMailingScreenEnabled;

  const isButtonDisabled = useMemo(() => {
    if (shouldNavigateToMailingAddress) {
      return false;
    }

    if (!isValid) {
      return true;
    }
    if (
      isSubmitEnabled &&
      !isDirty &&
      !isCheckboxDirty &&
      !hasAddressChanged()
    ) {
      return true;
    }
    if (!isSubmitEnabled && isHomeScreenType && !isCheckboxDirty) {
      return true;
    }
    if (!isSubmitEnabled && !isHomeScreenType) {
      return true;
    }

    return false;
  }, [
    isValid,
    isSubmitEnabled,
    isDirty,
    isCheckboxDirty,
    isHomeScreenType,
    hasAddressChanged,
    shouldNavigateToMailingAddress,
  ]);

  const handleMailingAddressCheckboxChange = () => {
    logMailingAddressCheckboxChange();
    setIsCheckedHomeAndMailingAddress?.((prev) => !prev);
  };

  const onSubmit = useCallback(async (): Promise<void> => {
    try {
      setIsSubmitEnabled(false);
      setShowBottomSheet(false);
      logAddressSubmit();
      if (shouldNavigateToMailingAddress) {
        router.push(
          '/myvaro/personal-details/change-mailing-address?analyticsKey=changeMailingAddress',
        );
        return;
      }

      const formData = getValues();

      const currentFormAddress: AddressInput = {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        country: 'US',
      };

      const toAddressInput = (source?: {
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
        country?: string | null;
      }): AddressInput | undefined =>
        source
          ? {
              addressLine1: source.addressLine1 ?? '',
              addressLine2: source.addressLine2 ?? '',
              city: source.city ?? '',
              state: source.state ?? '',
              zip: source.zip ?? '',
              country: source.country ?? 'US',
            }
          : undefined;

      let sourceAddress;

      if (isHomeAddressScreenType) {
        sourceAddress = mailingAddressOnFile;
      } else if (isMailingAddressDifferentThanResidential) {
        sourceAddress = addressOnFile;
      } else {
        sourceAddress =
          updatedAddressList?.find(
            (item) => item.addressType === AddressTypeInput.HomeAddress,
          )?.address ?? addressOnFile;
      }

      const compareAddress = toAddressInput(sourceAddress ?? undefined);

      const addressesAreSame = areAddressesIdentical(
        currentFormAddress,
        compareAddress,
      );
      if (isMailingScreenEnabled) {
        setIsIdenticalHomeAndMailingAddress?.(addressesAreSame);
      } else {
        setIsIdenticalHomeAndMailingAddress?.(false);
      }

      setUpdatedAddress({
        ...formData,
        country: 'US',
      });

      await refetchValidateAddress();
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        logBreadcrumb({
          level: 'error',
          message: 'Change address submit failed',
        });
      }
    }
  }, [
    getValues,
    isMailingScreenEnabled,
    isHomeAddressScreenType,
    mailingAddressOnFile,
    isMailingAddressDifferentThanResidential,
    addressOnFile,
    updatedAddressList,
    setUpdatedAddress,
    setIsIdenticalHomeAndMailingAddress,
    refetchValidateAddress,
    logAddressSubmit,
    shouldNavigateToMailingAddress,
    router,
  ]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      const actionType = e.data.action.type;

      if (
        pathname === '/myvaro/personal-details/change-mailing-address' &&
        actionType === ActionType.GOBACK
      ) {
        return;
      }
      setIsCheckedHomeAndMailingAddress?.(undefined);
    });

    return unsubscribe;
  }, [navigation, setIsCheckedHomeAndMailingAddress, pathname]);

  return (
    <>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          title: screenTitle,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <View
        style={[styles.container, { paddingBottom: insets.bottom }]}
        testID="inline-loader"
      >
        <ScrollView
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled={true}
          contentContainerStyle={[
            styles.scrollViewContentContainer,
            !isMailingSectionVisible
              ? styles.addressDropdownBottomPadding
              : null,
          ]}
        >
          {isHomeAddressScreenType && (
            <BodyBaseText>
              {isMailingScreenEnabled &&
                translatedText('changeAddress.subParaTextHomeAndMailing')}
              {!isMailingScreenEnabled &&
                translatedText('changeAddress.subParaText')}
            </BodyBaseText>
          )}
          <Controller
            control={control}
            name="addressLine1"
            render={({ field: { onChange, onBlur, value } }) => (
              <VXLSearchField
                testID="text-input-searchbox"
                labelText={translatedText('address.textField.addressLine1')}
                options={(filteredSuggestions ?? []).map((item, index) => ({
                  key: `${item.address_line}-${index}`,
                  label: item.address_line,
                  value: item,
                }))}
                keepMenuOpenOnSelect={(item) => item.entries > 1}
                onSelectOption={(item) => {
                  if (item.entries > 1) {
                    setSelectedSearchTerms(item);
                  } else {
                    setSelectedSearchTerms(undefined);
                    reset({
                      addressLine1: item?.street_line || '',
                      addressLine2: item?.secondary || '',
                      city: item?.city || '',
                      state: getStateAbbreviation(
                        statesData?.states ?? [],
                        item?.state ?? '',
                      ),
                      zip: item?.zipcode || '',
                    });
                    onChange(item?.street_line);
                    onBlur();
                  }
                  setIsSubmitEnabled(true);
                }}
                analyticsId="addressFieldClicked"
                placeholder=""
                value={value}
                onBlur={() => {
                  setSelectedSearchTerms(undefined);
                  onBlur();
                }}
                onChangeText={(text: string) => {
                  onChange(text);
                  setSearchTerms(text);
                  setIsSubmitEnabled(true);
                }}
                errorText={
                  errors.addressLine1?.message !== ''
                    ? errors.addressLine1?.message
                    : ''
                }
              />
            )}
          />
          <Controller
            control={control}
            name="addressLine2"
            render={({ field: { onChange, onBlur, value } }) => (
              <VXLTextInput
                testID="text-input-address.textField.addressLine2"
                errorText={errors.addressLine2?.message}
                labelText={translatedText('address.textField.addressLine2')}
                onChangeText={(text) => {
                  onChange(text);
                  onBlur();
                  setIsSubmitEnabled(true);
                }}
                value={value}
                autoCapitalize="none"
              />
            )}
          />
          <Controller
            control={control}
            name="city"
            render={({ field: { onChange, onBlur, value } }) => (
              <VXLTextInput
                testID="text-input-address.textField.city"
                errorText={errors.city?.message}
                labelText={translatedText('address.textField.city')}
                onChangeText={(text) => {
                  onChange(text);
                  onBlur();
                  setIsSubmitEnabled(true);
                }}
                value={value}
                autoCapitalize="none"
              />
            )}
          />
          {statesData?.states?.length ? (
            <Controller
              control={control}
              name="state"
              render={({ field: { onChange, onBlur, value } }) => (
                <VXLDropdown
                  key={value}
                  testID="address.textField.state-select"
                  labelText={translatedText('address.textField.state')}
                  placeholder={
                    findFullStateString(statesData.states, value) ??
                    translatedText('address.textField.state')
                  }
                  options={statesData.states.map(getState)}
                  value={value}
                  errorText={errors.state?.message}
                  onChangeText={(text) => {
                    onChange(getStateAbbreviation(statesData.states, text));
                    onBlur();
                    setIsSubmitEnabled(true);
                  }}
                />
              )}
            />
          ) : null}
          <Controller
            control={control}
            name="zip"
            render={({ field: { onChange, onBlur, value } }) => (
              <VXLTextInput
                testID="text-input-address.textField.zip"
                errorText={errors.zip?.message}
                labelText={translatedText('address.textField.zip')}
                onChangeText={(text) => {
                  onChange(text);
                  onBlur();
                  setIsSubmitEnabled(true);
                }}
                value={value}
                autoCapitalize="none"
                keyboardType="numeric"
              />
            )}
          />
          {isMailingSectionVisible && (
            <View>
              <View style={styles.sectionDivider} />
              <View style={styles.mailingAddressContainer}>
                <TitleSmText>
                  {translatedText('changeAddress.mailingAddress')}
                </TitleSmText>

                <View style={styles.checkBoxContainer}>
                  <Checkbox
                    checked={!!isCheckedHomeAndMailingAddress}
                    onSelectedStateChange={handleMailingAddressCheckboxChange}
                    testId="checkBoxClicked"
                    analyticsId={
                      !isCheckedHomeAndMailingAddress
                        ? 'mailingAddressDifferentChecked'
                        : 'mailingAddressDifferentUnchecked'
                    }
                  />

                  <BodyBaseText
                    style={styles.subParaTextMailing}
                    numberOfLines={0}
                  >
                    {translatedText('changeAddress.subParaTextMailing')}
                  </BodyBaseText>
                </View>
              </View>
            </View>
          )}
        </ScrollView>
        <View style={styles.buttonContainer}>
          <Button
            label={getButtonText}
            onPress={onSubmit}
            isLoading={fetching || loading}
            isDisabled={isButtonDisabled}
            size="LARGE"
            type="PRIMARY"
            analyticsId={changeAddressSubmitClickEvent}
            width="FULL"
          />
        </View>
        {showBottomSheet && (
          <AddressVerificationScreen
            bottomSheetRef={bottomSheetRef}
            setShowBottomSheet={setShowBottomSheet}
          />
        )}
      </View>
      <ScreenTransitionOverlay
        isLoading={loadingStates || fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={null}
      />
    </>
  );
};

const styles = StyleSheet.create({
  addressDropdownBottomPadding: {
    paddingBottom: VXL_DROPDOWN_MENU_RESERVED_SPACE,
  },
  buttonContainer: {
    padding: theme.spacing.m,
  },
  checkBoxContainer: {
    backgroundColor: theme.color.background.primary,
    flexDirection: 'row',
    gap: theme.spacing.m,
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  mailingAddressContainer: {
    marginBottom: theme.spacing.m,
    paddingLeft: theme.spacing.m,
  },
  scrollViewContentContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
    marginBottom: theme.spacing.m,
  },
  subParaTextMailing: {
    marginTop: theme.spacing.s,
    paddingBottom: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
    paddingLeft: 0,
    flex: 1,
    flexShrink: 1,
  },
});

export default ChangeAddressScreen;
